import React, { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useImmer } from "use-immer";

import {
  IgButton,
  IgDialog,
  IgRadio,
  IgTypography,
} from "@intelligems/ig-theme";
import { ExperienceDto, GenericExperienceFe } from "@intelligems/ig-types";
import { ExperienceFe } from "@intelligems/ig-types/fe-only/experiences";
import { FormControlLabel, Grid, Radio, RadioGroup } from "@mui/material";
import { useEndExperience } from "@src/api/experience-actions/end-experience";
import { getDefaultHiddenEndTime } from "@src/store/helpers";
import { updateDashboardFilter } from "@src/store/slices/dashboardFilters";
import {
  getDialogPropDetails,
  isDialogOpen,
  setDialog,
} from "@src/store/slices/global";
import {
  endShippingTest,
  personalizationRollingOutTests,
} from "@src/utils/linkSheet";
import { openNewTab } from "@src/utils/openNewTab";

interface EndTestDialogProps {
  experience: GenericExperienceFe;
}

export const EndNonPriceTestDialog = ({ experience }: EndTestDialogProps) => {
  const dispatch = useDispatch();
  const dialogProps = useSelector(getDialogPropDetails("endNonPriceTest"));
  const endExperience = useEndExperience(true, experience.id);
  const isOpen = useSelector(isDialogOpen("endNonPriceTest"));
  const handleClose = () => {
    dispatch(setDialog({ dialog: "endNonPriceTest", open: false }));
  };

  const [shouldRolloutChanges, setShouldRolloutChanges] = useImmer(
    !!(dialogProps?.defaultToRollout && !experience.testTypes.hasTestShipping)
  );

  const onHandleYesClick = () => {
    if (shouldRolloutChanges) {
      dispatch(
        updateDashboardFilter({
          key: "hiddenEndTime",
          value: getDefaultHiddenEndTime(experience),
        })
      );

      dispatch(setDialog({ dialog: "rollouts", open: true }));
    } else {
      endExperience.mutate({
        body: { variationId: undefined },
        queryParams: undefined,
        pathParams: {
          experienceId: experience.id!,
          name: experience.name,
          action: "end",
        },
      });
    }
    handleClose();
  };
  const rawContent = useMemo(() => {
    if (experience.testTypes.hasTestShipping) {
      return (
        <Grid
          item
          container
          direction={"column"}
          textAlign={"start"}
          rowSpacing={2}
        >
          <Grid item>
            <IgTypography>Are you sure you want to end this test?</IgTypography>
          </Grid>
          <br />
          <Grid item>
            <IgTypography>
              Once ended, shipping rates will be returned back to normal. If
              you’d like to change them based on the results of the test, make
              sure to update them in Shopify.{" "}
              <IgTypography
                onClick={() => {
                  openNewTab(endShippingTest, "How to End a Shipping Test");
                }}
                igStyle={"linkNoBold"}
              >
                Learn more about ending shipping tests.
              </IgTypography>
            </IgTypography>
          </Grid>
        </Grid>
      );
    } else {
      return (
        <Grid
          item
          container
          direction={"column"}
          textAlign={"start"}
          rowSpacing={2}
        >
          <Grid item>
            <IgTypography>
              Once the test is ended, would you like to roll out the best
              performing group?
              <IgTypography
                onClick={() => {
                  openNewTab(
                    personalizationRollingOutTests,
                    "Rolling Out Tests" // check that this is the correct mixpanel tracker
                  );
                }}
                igStyle={"linkNoBold"}
              >
                {" "}
                Find out more.
              </IgTypography>
            </IgTypography>
          </Grid>
          <Grid item>
            <RadioGroup
              value={shouldRolloutChanges}
              aria-labelledby="radio-buttons-tier-group-label"
              defaultValue="currency"
              name="radio-buttons-tier-group"
              onChange={(e) => {
                setShouldRolloutChanges(e.target.value === "true");
              }}
            >
              <FormControlLabel
                value={false}
                control={<IgRadio />}
                label="No, just end the test."
              />
              <FormControlLabel
                value={true}
                control={<IgRadio />}
                label="Yes, roll out changes."
              />
            </RadioGroup>
          </Grid>
          <Grid
            item
            container
            direction={"row"}
            justifyContent={"space-between"}
          >
            <Grid item>
              <IgButton onClick={handleClose}>Cancel</IgButton>
            </Grid>
            <Grid item>
              <IgButton onClick={onHandleYesClick} variant={"contained"}>
                {shouldRolloutChanges ? "Go to Rollout Wizard" : "End Test"}
              </IgButton>
            </Grid>
          </Grid>
        </Grid>
      );
    }
  }, [experience.testTypes, shouldRolloutChanges]);

  return (
    <IgDialog
      title={"End Test"}
      rawContent={rawContent}
      handleClose={handleClose}
      handleYesClick={onHandleYesClick}
      showDialog={isOpen}
      yesButtonText={shouldRolloutChanges ? "Go to Rollout Wizard" : "End Test"}
      dialogHeight={null}
      hideActions={!experience.testTypes.hasTestShipping}
      noButtonText={"Cancel"}
      subtitleSx={{ width: "250px", marginBottom: "10px" }}
    />
  );
};
